/**
 * Avatar-Component (Phase 3.6) — Bild ODER Initialen-Fallback.
 *
 * DSGVO-Lockdown (2026-05-25): der Bild-Endpoint ist auth-gated, ein `<img src>`
 * kann keinen Bearer mitschicken. Daher rendert `avatarHtml()` synchron NUR die
 * Initialen-Hülle (mit `data-av-*`-Attributen), und `hydrateAvatars()` lädt das
 * Bild async per `fetch(Authorization)` → `URL.createObjectURL(blob)` nach.
 *
 * Blob-URLs werden modulweit gecacht (key = `person_id:token`), damit Footer,
 * Rail und Owner-Pills dasselbe Bild nicht mehrfach laden. `invalidateAvatar()`
 * gibt die Blobs einer Person nach Upload/Delete frei. Der Worker liefert
 * `Cache-Control: private` + ETag → der erste Fetch nutzt zusätzlich den
 * Browser-HTTP-Cache.
 */

import { profileImageUrl, kbImageUrl } from "../api/client";
import { getToken } from "../auth/token";

export function computeInitials(vollname?: string, email?: string): string {
  const name = (vollname ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
    }
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  const e = (email ?? "").trim();
  return e ? e.slice(0, 2).toUpperCase() : "?";
}

export type AvatarOpts = {
  vollname?: string;
  email?: string;
  farbe?: string;
  profilbildKey?: string;
  personId?: string;
  size?: number;
  className?: string;
};

/**
 * Synchroner Initialen-Platzhalter. Liegt ein Bild vor (Token + person_id),
 * werden `data-av-*`-Attribute gesetzt — `hydrateAvatars()` lädt das Bild dann
 * nach. Ohne Bild bleibt es bei den Initialen (kein Fetch).
 */
export function avatarHtml(o: AvatarOpts): string {
  const initials = computeInitials(o.vollname, o.email);
  const bg = (o.farbe && o.farbe.trim()) || "#5b6b7a";
  const size = o.size ?? 32;
  const cls = ["ct-avatar", o.className ?? ""].filter(Boolean).join(" ");
  const data =
    o.profilbildKey && o.personId
      ? ` data-av-person="${escapeAttr(o.personId)}" data-av-token="${escapeAttr(o.profilbildKey)}"`
      : "";
  return (
    `<span class="${cls}" style="--av-size:${size}px;--av-bg:${escapeAttr(bg)}"${data} aria-hidden="true">` +
    `<span class="ct-avatar-initials">${escapeHtml(initials)}</span></span>`
  );
}

// =============================================================================
// Async-Hydration (fetch + Blob), modulweit gecacht
// =============================================================================

const blobCache = new Map<string, string>(); // "person_id:token" → objectURL
const inflight = new Map<string, Promise<string | null>>();

function cacheKey(pid: string, token: string): string {
  return `${pid}:${token}`;
}

async function loadAvatarBlob(pid: string, token: string): Promise<string | null> {
  const key = cacheKey(pid, token);
  const cached = blobCache.get(key);
  if (cached) return cached;
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = (async (): Promise<string | null> => {
    const tok = getToken();
    if (!tok) return null;
    try {
      const res = await fetch(profileImageUrl(pid, token), {
        headers: { Authorization: `Bearer ${tok}` }
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobCache.set(key, url);
      return url;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/**
 * Lädt für alle noch nicht hydratisierten Avatare unter `root` das Bild nach und
 * legt es als `<img>` über die Initialen. Idempotent: konsumiert `data-av-token`,
 * sodass mehrfaches Aufrufen nicht doppelt fetcht.
 */
export async function hydrateAvatars(root: ParentNode = document): Promise<void> {
  const els = Array.from(root.querySelectorAll<HTMLElement>(".ct-avatar[data-av-token]"));
  await Promise.all(
    els.map(async (el) => {
      const pid = el.getAttribute("data-av-person") ?? "";
      const token = el.getAttribute("data-av-token") ?? "";
      el.removeAttribute("data-av-token"); // als bearbeitet markieren
      if (!pid || !token) return;
      const url = await loadAvatarBlob(pid, token);
      if (!url) return; // Fehler / abgemeldet → Initialen bleiben
      if (el.querySelector(".ct-avatar-img")) return; // schon gesetzt
      const img = document.createElement("img");
      img.className = "ct-avatar-img";
      img.alt = "";
      img.src = url;
      el.appendChild(img);
    })
  );
}

// =============================================================================
// Vereins-Bilder (KB) — gleiche Blob-Cache-Mechanik, eigener Key-Namespace
// =============================================================================

async function loadClubBlob(companyId: string, token: string): Promise<string | null> {
  const key = cacheKey(`club:${companyId}`, token);
  const cached = blobCache.get(key);
  if (cached) return cached;
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = (async (): Promise<string | null> => {
    const tok = getToken();
    if (!tok) return null;
    try {
      const res = await fetch(kbImageUrl(companyId, token), { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) return null;
      const url = URL.createObjectURL(await res.blob());
      blobCache.set(key, url);
      return url;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/**
 * Hydratisiert Vereins-Avatare: lädt für `.kb-avatar[data-kb-token]` das Bild per
 * fetch(Authorization) nach und legt es als `<img class="kb-avatar-img">` über das
 * Monogramm. Idempotent (konsumiert `data-kb-token`).
 */
export async function hydrateClubAvatars(root: ParentNode = document): Promise<void> {
  const els = Array.from(root.querySelectorAll<HTMLElement>(".kb-avatar[data-kb-token]"));
  await Promise.all(
    els.map(async (el) => {
      const companyId = el.getAttribute("data-kb-company") ?? "";
      const token = el.getAttribute("data-kb-token") ?? "";
      el.removeAttribute("data-kb-token");
      if (!companyId || !token) return;
      const url = await loadClubBlob(companyId, token);
      if (!url) return;
      if (el.querySelector(".kb-avatar-img")) return;
      const img = document.createElement("img");
      img.className = "kb-avatar-img";
      img.alt = "";
      img.src = url;
      el.appendChild(img);
    })
  );
}

/** Gibt gecachte Blob-URLs eines Vereins frei (nach Upload/Delete). */
export function invalidateClubAvatar(companyId: string): void {
  const prefix = `club:${companyId}:`;
  for (const key of Array.from(blobCache.keys())) {
    if (key.startsWith(prefix)) {
      const url = blobCache.get(key);
      if (url) URL.revokeObjectURL(url);
      blobCache.delete(key);
    }
  }
}

/** Gibt gecachte Blob-URLs einer Person frei (nach Upload/Delete). */
export function invalidateAvatar(pid: string): void {
  for (const key of Array.from(blobCache.keys())) {
    if (key.startsWith(`${pid}:`)) {
      const url = blobCache.get(key);
      if (url) URL.revokeObjectURL(url);
      blobCache.delete(key);
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
